import { Stepper } from '@mantine/core'
import { useCallback, useEffect, useState } from 'react'
import { CalendarInput } from './components/CalendarInput'
import SlotsGrid from './components/SlotsGrid/SlotsGrid'
import { Patient, Encounter } from '@medplum/fhirtypes'
import { ConfirmAppointmentCard } from './components/ConfirmAppointmentCard'
import { ConfirmAppointmentReschedule } from './components/ConfirmAppointmentReschedule'
import { AppointmentSuccessCard } from './components/AppointmentSuccessCard'
import { normalizeErrorString } from '@medplum/core'
import { cancelAppointment } from '@/services/call-center-service'
import { Slot } from '@medplum/fhirtypes'
import { useMedplum } from '@medplum/react'
import { ZOCDOC_APPOINTMENT_SYSTEM } from '@/model/middleware-types'
import { formatDateTime, formatPatientName } from '@/utils.ts'
import { showNotification } from '@mantine/notifications'
import { useMiddlewareClient } from '@/hooks'
import { logError } from '@/utils/errorUtils'

type BookAppointmentResponse = {
  success: boolean
  slot?: Slot
  practitionerName?: string
  patientName?: string
  message?: string
}

function getBookingErrorMessage(error: unknown, fallback: string): string {
  if (typeof error === 'object' && error !== null) {
    const message = (error as { message?: unknown }).message

    if (typeof message === 'string' && message.trim()) {
      return message
    }

    if (Array.isArray(message) && message.length > 0) {
      return message.filter((item): item is string => typeof item === 'string').join(', ')
    }
  }

  return normalizeErrorString(error) || fallback
}

export const BookAppointmentStepper = ({
  completeBookedSlot,
  setCompleteBookedSlot,
  selectedPatient,
  closeStepper,
  slotToReschedule,
}: {
  completeBookedSlot: {
    success: boolean
    slot?: Slot
    practitionerName?: string
    patientName?: string
  } | null
  setCompleteBookedSlot: (
    value: {
      success: boolean
      slot?: Slot
      practitionerName?: string
      patientName?: string
    } | null,
  ) => void
  selectedPatient: Patient | null
  setSelectedPatient: (patient: Patient | null) => void
  closeStepper: () => void
  slotToReschedule?: Encounter | null
}) => {
  const [activeStep, setActiveStep] = useState(0)
  const [availableSlots, setAvailableSlots] = useState<Slot[]>([])
  const [selectedSlot, setSelectedSlot] = useState<Slot | null>(null)
  const [date, setDate] = useState<Date | null>(null)
  const [isLoadingSlots, setIsLoadingSlots] = useState<boolean>(false)
  const [isBookingSlot, setIsBookingSlot] = useState<boolean>(false)
  const [month, setMonth] = useState<Date>(new Date())
  const medplum = useMedplum()
  const middleware = useMiddlewareClient()

  const getAvailableSlots = useCallback(
    async (month: Date) => {
      try {
        setIsLoadingSlots(true)
        const normalizedMonth = new Date(month.getFullYear(), month.getMonth(), 1).toISOString()
        const result = (await middleware.get('/appointment/slots', {
          month: normalizedMonth,
        })) as { slots: Slot[] }
        setAvailableSlots(result.slots ?? [])
      } catch (error) {
        logError('call-center', 'Error fetching available slots', error)
      } finally {
        setIsLoadingSlots(false)
      }
    },
    [middleware],
  )
  const handleSlotBooking = async () => {
    if (!selectedSlot || !selectedPatient || !selectedPatient?.id) {
      return
    }
    setIsBookingSlot(true)
    try {
      const slotBookingResult = (await middleware.post('/appointment/book', {
        patientId: selectedPatient.id,
        slot: selectedSlot,
      })) as BookAppointmentResponse
      if (slotBookingResult && slotBookingResult?.success) {
        setCompleteBookedSlot(slotBookingResult)
        setActiveStep(4)
        showNotification({
          color: 'green',
          title: 'Success',
          message: 'Appointment booked successfully',
        })
      } else {
        showNotification({
          color: 'red',
          title: 'Unable to book appointment',
          message: slotBookingResult?.message || 'Unable to book appointment. Please try again.',
        })
      }
    } catch (error) {
      logError('call-center', 'Error booking slot', error)
      showNotification({
        color: 'red',
        title: 'Unable to book appointment',
        message: getBookingErrorMessage(error, 'Unable to book appointment. Please try again.'),
      })
    } finally {
      setIsBookingSlot(false)
    }
  }
  const handleSlotRescheduling = async () => {
    if (!slotToReschedule || !selectedSlot || !selectedPatient || !selectedPatient?.id) {
      return
    }
    setIsBookingSlot(true)
    try {
      // Read the Zocdoc link before the cancel: cancelling hard-deletes the Appointment that
      // carries it, so without this the rebooked visit is orphaned and no further Zocdoc event
      // can find it. A failure here must not block the reschedule — an unlinked visit is better
      // than a stranded patient, and it can be re-linked by hand.
      let zocdocAppointmentId: string | undefined
      try {
        const appointmentId = slotToReschedule.appointment?.[0]?.reference?.split('/')[1]
        if (appointmentId) {
          const appointment = await medplum.readResource('Appointment', appointmentId)
          zocdocAppointmentId = appointment.identifier?.find(
            (candidate) => candidate.system === ZOCDOC_APPOINTMENT_SYSTEM,
          )?.value
        }
      } catch {
        // Continue unlinked.
      }

      const cancelationResult = await cancelAppointment(middleware, slotToReschedule.id as string)
      if (!cancelationResult) {
        throw new Error('Failed to cancel the existing appointment for rescheduling.')
      }
      const slotBookingResult = (await middleware.post('/appointment/book', {
        patientId: selectedPatient.id,
        slot: selectedSlot,
        ...(zocdocAppointmentId && { zocdocAppointmentId }),
      })) as BookAppointmentResponse
      if (slotBookingResult && slotBookingResult?.success) {
        setCompleteBookedSlot(slotBookingResult)
        setActiveStep(4)
        showNotification({
          color: 'green',
          title: 'Success',
          message: 'Appointment rescheduled successfully',
        })
      } else {
        showNotification({
          color: 'red',
          title: 'Unable to reschedule appointment',
          message:
            slotBookingResult?.message || 'Unable to reschedule appointment. Please try again.',
        })
      }
    } catch (error) {
      logError('call-center', 'Error rescheduling slot', error)
      showNotification({
        color: 'red',
        title: 'Unable to reschedule appointment',
        message: getBookingErrorMessage(
          error,
          'Unable to reschedule appointment. Please try again.',
        ),
      })
    } finally {
      setIsBookingSlot(false)
    }
  }
  useEffect(() => {
    getAvailableSlots(month)
  }, [getAvailableSlots, month])
  return (
    <div className="flex flex-col gap-4 text-center text-2xl font-semibold">
      <Stepper
        size="lg"
        active={activeStep}
        onStepClick={setActiveStep}
        allowNextStepsSelect={false}
        styles={{
          steps: {
            height: 50,
            width: 600,
          },
          stepBody: {
            display: 'none',
          },

          step: {
            padding: 0,
          },

          stepIcon: {
            borderWidth: 4,
          },

          separator: {
            marginLeft: -2,
            marginRight: -2,
            height: 2,
          },
        }}
      >
        <Stepper.Step label="Choose Date">
          <CalendarInput
            onBack={closeStepper}
            slots={availableSlots || []}
            defaultMonth={month}
            onChangeMonth={setMonth}
            isLoadingSlots={isLoadingSlots}
            onClick={(date) => {
              setDate(date)
              setActiveStep(1)
            }}
          />
        </Stepper.Step>
        <Stepper.Step label="Choose time slot">
          <SlotsGrid
            date={date as Date}
            onBack={() => setActiveStep(0)}
            onSlotClick={(slot) => {
              setSelectedSlot(slot)
              setActiveStep(2)
            }}
          />
        </Stepper.Step>
        <Stepper.Step label="Confirm Appointment">
          {slotToReschedule?.id ? (
            <ConfirmAppointmentReschedule
              dateLabel={selectedSlot?.start && formatDateTime(selectedSlot?.start)}
              timeLabel={new Date(selectedSlot?.start as string)
                .toLocaleTimeString([], {
                  hour: 'numeric',
                  minute: '2-digit',
                  hour12: true,
                })
                .toLocaleUpperCase()}
              onBack={() => setActiveStep(1)}
              onConfirm={handleSlotRescheduling}
              loading={isBookingSlot}
              oldDateLabel={
                slotToReschedule?.period?.start ? formatDateTime(slotToReschedule.period.start) : ''
              }
              oldTimeLabel={
                slotToReschedule?.period?.start
                  ? new Date(slotToReschedule.period.start)
                      .toLocaleTimeString([], {
                        hour: 'numeric',
                        minute: '2-digit',
                        hour12: true,
                      })
                      .toLocaleUpperCase()
                  : ''
              }
            />
          ) : (
            <ConfirmAppointmentCard
              patientName={formatPatientName(selectedPatient?.name?.[0])}
              dateLabel={selectedSlot?.start && formatDateTime(selectedSlot?.start)}
              timeLabel={new Date(selectedSlot?.start as string)
                .toLocaleTimeString([], {
                  hour: 'numeric',
                  minute: '2-digit',
                  hour12: true,
                })
                .toLocaleUpperCase()}
              onBack={() => setActiveStep(1)}
              onConfirm={handleSlotBooking}
              loading={isBookingSlot}
            />
          )}
        </Stepper.Step>
        <Stepper.Completed>
          <section className="flex flex-col items-center gap-4">
            {completeBookedSlot && (
              <AppointmentSuccessCard
                doctorName={completeBookedSlot?.practitionerName || ''}
                patientName={completeBookedSlot?.patientName || ''}
                isBookingAnotherAppointmentEnabled={slotToReschedule === null}
                dateLabel={
                  (completeBookedSlot?.slot && formatDateTime(completeBookedSlot?.slot.start)) || ''
                }
                timeLabel={
                  (completeBookedSlot?.slot &&
                    new Date(completeBookedSlot?.slot.start)
                      .toLocaleTimeString([], {
                        hour: 'numeric',
                        minute: '2-digit',
                        hour12: true,
                      })
                      .toLocaleUpperCase()) ||
                  ''
                }
                onBookAnother={() => {
                  setActiveStep(0)
                  setDate(null)
                  setSelectedSlot(null)
                  setCompleteBookedSlot(null)
                }}
                closeStepper={closeStepper}
              />
            )}
          </section>
        </Stepper.Completed>
      </Stepper>
    </div>
  )
}
